/**
 * In-memory session secrets only (ADR-009).
 * Never persist access / refresh / step-up / recovery codes.
 */
export type SessionUser = {
  id: string
  email: string
  is_admin: boolean
  tenant_id: string
  display_name?: string | null
  has_avatar?: boolean
}

export type SessionSnapshot = {
  accessToken: string | null
  user: SessionUser | null
  amr: string[]
  sessionId: string | null
  bootstrapped: boolean
  hasStepUp: boolean
}

type Listener = () => void

let accessToken: string | null = null
let stepUpToken: string | null = null
let stepUpExpiresAt = 0
let user: SessionUser | null = null
let amr: string[] = []
let sessionId: string | null = null
let bootstrapped = false

/** Stable snapshot for useSyncExternalStore (same ref until mutation). */
let cached: SessionSnapshot = {
  accessToken: null,
  user: null,
  amr: [],
  sessionId: null,
  bootstrapped: false,
  hasStepUp: false,
}

const listeners = new Set<Listener>()

function rebuildCache() {
  cached = {
    accessToken,
    user,
    amr,
    sessionId,
    bootstrapped,
    hasStepUp: Boolean(stepUpToken && Date.now() < stepUpExpiresAt),
  }
}

function emit() {
  rebuildCache()
  listeners.forEach((l) => l())
}

export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getAccessToken(): string | null {
  return accessToken
}

export function getStepUpToken(): string | null {
  if (!stepUpToken || Date.now() >= stepUpExpiresAt) {
    if (stepUpToken) {
      stepUpToken = null
      emit()
    }
    return null
  }
  return stepUpToken
}

export function getSessionUser(): SessionUser | null {
  return user
}

export function getAmr(): string[] {
  return amr
}

export function getSessionId(): string | null {
  return sessionId
}

export function isBootstrapped(): boolean {
  return bootstrapped
}

export function setBootstrapped(v: boolean) {
  if (bootstrapped === v) return
  bootstrapped = v
  emit()
}

export function setSession(opts: {
  accessToken: string
  user: SessionUser
  amr?: string[]
  sessionId?: string | null
}) {
  accessToken = opts.accessToken
  user = opts.user
  amr = opts.amr ?? []
  sessionId = opts.sessionId ?? null
  emit()
}

export function clearSession() {
  accessToken = null
  stepUpToken = null
  stepUpExpiresAt = 0
  user = null
  amr = []
  sessionId = null
  emit()
}

export function setStepUp(token: string, expiresInSec: number) {
  stepUpToken = token
  stepUpExpiresAt = Date.now() + Math.max(30, expiresInSec - 15) * 1000
  emit()
}

export function clearStepUp() {
  stepUpToken = null
  stepUpExpiresAt = 0
  emit()
}

export function getSnapshot(): SessionSnapshot {
  return cached
}

export function snapshot(): SessionSnapshot {
  return cached
}
